<template>
  <div class="esg-analytics-container">
    <!-- Animated Background -->
    <div class="animated-bg">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
      <div class="gradient-mesh"></div>
    </div>

    <!-- Main Content -->
    <div class="esg-content-wrapper">
      <!-- Hero Section -->
      <section class="esg-hero">
        <div class="hero-badge">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="badge-icon">
            <path d="M10 2L12 7H17L13 10L15 15L10 12L5 15L7 10L3 7H8L10 2Z" fill="currentColor"/>
          </svg>
          <span class="badge-text">{{ t('esg.badge') }}</span>
        </div>
        <h1 class="hero-title">{{ t('esg.title') }}</h1>
        <p class="hero-subtitle">{{ t('esg.subtitle') }}</p>
      </section>

      <!-- Real-time Metrics -->
      <section class="metrics-section">
        <div class="metrics-grid">
          <div class="metric-card" v-for="(metric, index) in realtimeMetrics" :key="index"
               :style="{ animationDelay: `${index * 0.1}s` }">
            <div class="metric-header">
              <div class="metric-icon" :style="{ background: metric.gradient }">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path :d="metric.iconPath" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="metric-trend" :class="metric.trendClass">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path :d="metric.trendClass === 'positive'
                    ? 'M8 12V4M8 4L4 8M8 4l4 4'
                    : 'M8 4v8M8 12l-4-4M8 12l4-4'"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ metric.trend }}</span>
              </div>
            </div>
            <h3 class="metric-label">{{ metric.label }}</h3>
            <div class="metric-value-row">
              <span class="metric-value">{{ metric.value }}</span>
              <span class="metric-unit">{{ metric.unit }}</span>
            </div>
            <p class="metric-description">{{ metric.description }}</p>
          </div>
        </div>
      </section>

      <!-- Environmental Monitoring -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon environmental">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <path d="M20 8C20 8 12 14 12 20C12 23.3 14.7 26 18 27.5M20 8C20 8 28 14 28 20C28 23.3 25.3 26 22 27.5M20 8V32"
                    stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="section-text">
            <h2 class="section-title">{{ t('esg.environmental.title') }}</h2>
            <p class="section-subtitle">{{ t('esg.environmental.subtitle') }}</p>
          </div>
        </div>

        <div class="feature-content">
          <!-- Energy Dashboard -->
          <article class="analytics-card large">
            <div class="card-header">
              <div class="header-left">
                <h3 class="card-title">{{ t('esg.environmental.energy.title') }}</h3>
                <p class="card-subtitle">{{ t('esg.environmental.energy.subtitle') }}</p>
              </div>
              <div class="header-badge success">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>{{ t('esg.environmental.energy.status') }}</span>
              </div>
            </div>
            <div class="analytics-visual">
              <img src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=1200&h=400&fit=crop"
                   alt="Energy monitoring" class="feature-bg-image" />
              <div class="visual-overlay">
                <div class="stat-boxes">
                  <div class="stat-box">
                    <span class="stat-value">2,847</span>
                    <span class="stat-label">kWh {{ t('esg.common.today') }}</span>
                  </div>
                  <div class="stat-box">
                    <span class="stat-value">-18%</span>
                    <span class="stat-label">{{ t('esg.common.vsLastMonth') }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-description">
              <p>{{ t('esg.environmental.energy.description') }}</p>
            </div>
          </article>

          <!-- Energy Consumption Chart -->
          <article class="analytics-card">
            <div class="card-header-simple">
              <h3 class="card-title-simple">{{ t('esg.environmental.energy.chart.title') }}</h3>
            </div>
            <div class="chart-wrapper">
              <apexchart type="line" height="280" :options="energyChartOptions" :series="energyChartSeries" />
            </div>
          </article>

          <!-- Room Occupancy & Climate -->
          <article class="analytics-card">
            <div class="card-header-simple">
              <h3 class="card-title-simple">{{ t('esg.environmental.climate.title') }}</h3>
            </div>
            <div class="climate-grid">
              <div class="climate-item">
                <div class="climate-icon temp">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path d="M14 4v12m0 0a3 3 0 100 6 3 3 0 000-6z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>
                <div class="climate-data">
                  <span class="climate-value">21.5°C</span>
                  <span class="climate-label">{{ t('esg.environmental.climate.temperature') }}</span>
                </div>
              </div>
              <div class="climate-item">
                <div class="climate-icon humidity">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path d="M14 4s-8 8-8 12a8 8 0 0016 0c0-4-8-12-8-12z" stroke="currentColor" stroke-width="2"/>
                  </svg>
                </div>
                <div class="climate-data">
                  <span class="climate-value">45%</span>
                  <span class="climate-label">{{ t('esg.environmental.climate.humidity') }}</span>
                </div>
              </div>
              <div class="climate-item">
                <div class="climate-icon co2">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <circle cx="14" cy="14" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M14 8v8l4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>
                <div class="climate-data">
                  <span class="climate-value">420 ppm</span>
                  <span class="climate-label">{{ t('esg.environmental.climate.co2') }}</span>
                </div>
              </div>
              <div class="climate-item">
                <div class="climate-icon occupancy">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path d="M20 24v-2a4 4 0 00-4-4H12a4 4 0 00-4 4v2M14 12a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>
                <div class="climate-data">
                  <span class="climate-value">68%</span>
                  <span class="climate-label">{{ t('esg.environmental.climate.occupancy') }}</span>
                </div>
              </div>
            </div>
            <div class="card-footer-info">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
                <path d="M8 4v4l2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <span>{{ t('esg.environmental.climate.updated') }}</span>
            </div>
          </article>

          <!-- Resource Optimization -->
          <article class="analytics-card">
            <div class="card-header-simple">
              <h3 class="card-title-simple">{{ t('esg.environmental.resources.title') }}</h3>
            </div>
            <div class="resource-list">
              <div class="resource-item" v-for="(resource, index) in resources" :key="index">
                <div class="resource-info">
                  <div class="resource-icon" :style="{ background: resource.color }">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path :d="resource.iconPath" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <div class="resource-details">
                    <span class="resource-name">{{ t(resource.name) }}</span>
                    <span class="resource-value">{{ resource.value }}</span>
                  </div>
                </div>
                <div class="resource-progress">
                  <div class="progress-bar-mini">
                    <div class="progress-fill-mini" :style="{ width: resource.percentage + '%', background: resource.color }"></div>
                  </div>
                  <span class="resource-percentage">{{ resource.percentage }}%</span>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Social Impact -->
      <section class="feature-section social-section">
        <div class="section-header">
          <div class="section-icon social">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <path d="M28 32v-4a6 6 0 00-6-6h-8a6 6 0 00-6 6v4M18 16a6 6 0 100-12 6 6 0 000 12z"
                    stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="section-text">
            <h2 class="section-title">{{ t('esg.social.title') }}</h2>
            <p class="section-subtitle">{{ t('esg.social.subtitle') }}</p>
          </div>
        </div>

        <div class="feature-content">
          <!-- Space Utilization -->
          <article class="analytics-card large">
            <div class="card-header-simple">
              <h3 class="card-title-simple">{{ t('esg.social.space.title') }}</h3>
            </div>
            <div class="chart-wrapper">
              <apexchart type="bar" height="300" :options="spaceChartOptions" :series="spaceChartSeries" />
            </div>
          </article>

          <!-- Wellbeing Metrics -->
          <article class="analytics-card">
            <div class="card-header-simple">
              <h3 class="card-title-simple">{{ t('esg.social.wellbeing.title') }}</h3>
            </div>
            <div class="wellbeing-grid">
              <div class="wellbeing-card" v-for="(item, index) in wellbeingMetrics" :key="index">
                <div class="wellbeing-icon" :style="{ background: item.gradient }">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path :d="item.iconPath" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <span class="wellbeing-label">{{ t(item.label) }}</span>
                <span class="wellbeing-value" :style="{ color: item.color }">{{ item.value }}</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- Governance -->
      <section class="feature-section governance-section">
        <div class="section-header">
          <div class="section-icon governance">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <rect x="8" y="12" width="24" height="20" rx="2" stroke="currentColor" stroke-width="2.5"/>
              <path d="M14 12V10a4 4 0 018 0v2M20 22v2" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
              <circle cx="20" cy="20" r="1.5" fill="currentColor"/>
            </svg>
          </div>
          <div class="section-text">
            <h2 class="section-title">{{ t('esg.governance.title') }}</h2>
            <p class="section-subtitle">{{ t('esg.governance.subtitle') }}</p>
          </div>
        </div>

        <div class="feature-content">
          <!-- Compliance Dashboard -->
          <article class="analytics-card">
            <div class="card-header-simple">
              <h3 class="card-title-simple">{{ t('esg.governance.compliance.title') }}</h3>
            </div>
            <div class="compliance-list">
              <div class="compliance-item" v-for="(item, index) in complianceItems" :key="index">
                <div class="compliance-left">
                  <div class="compliance-status" :class="item.status">
                    <svg v-if="item.status === 'active'" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="2"/>
                    </svg>
                  </div>
                  <div class="compliance-info">
                    <span class="compliance-name">{{ t(item.name) }}</span>
                    <span class="compliance-detail">{{ t(item.detail) }}</span>
                  </div>
                </div>
                <span class="compliance-date">{{ item.date }}</span>
              </div>
            </div>
          </article>

          <!-- Automated Controls -->
          <article class="analytics-card">
            <div class="card-header-simple">
              <h3 class="card-title-simple">{{ t('esg.governance.automation.title') }}</h3>
            </div>
            <div class="chart-wrapper">
              <apexchart type="donut" height="280" :options="automationChartOptions" :series="automationChartSeries" />
            </div>
          </article>
        </div>
      </section>

      <!-- CTA -->
      <section class="cta-section">
        <div class="cta-content">
          <div class="cta-icon">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
              <path d="M28 14v28m0 0l-8-8m8 8l8-8" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="28" cy="28" r="24" stroke="currentColor" stroke-width="3"/>
            </svg>
          </div>
          <h2 class="cta-title">{{ t('esg.cta.title') }}</h2>
          <p class="cta-description">{{ t('esg.cta.description') }}</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Real-time Metrics — Labels & Descriptions are translated
const realtimeMetrics = ref([
  {
    label: t('esg.metrics.energy'),
    value: '2,847',
    unit: 'kWh',
    trend: '-18%',
    trendClass: 'positive',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    iconPath: 'M13 2L3 14h8l-1 8 10-12h-8l1-8z',
    description: t('esg.metrics.energyDesc')
  },
  {
    label: t('esg.metrics.occupancy'),
    value: '68',
    unit: '%',
    trend: '+5%',
    trendClass: 'neutral',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    iconPath: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
    description: t('esg.metrics.occupancyDesc')
  },
  {
    label: t('esg.metrics.airQuality'),
    value: '98',
    unit: 'AQI',
    trend: '+2%',
    trendClass: 'positive',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    iconPath: 'M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22',
    description: t('esg.metrics.airQualityDesc')
  },
  {
    label: t('esg.metrics.efficiency'),
    value: '92',
    unit: '%',
    trend: '+12%',
    trendClass: 'positive',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    description: t('esg.metrics.efficiencyDesc')
  }
])

// Energy Chart
const energyChartSeries = ref([{ name: t('esg.environmental.energy.chart.name'), data: [2200, 2400, 2100, 2300, 2000, 2200, 2100, 1900, 2000, 1850, 1900, 1847] }])
const energyChartOptions = computed(() => ({
  chart: { type: 'line', height: 280, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
  colors: ['#10b981'],
  stroke: { curve: 'smooth', width: 3 },
  dataLabels: { enabled: false },
  xaxis: { categories: t('esg.common.months').split(','), labels: { style: { colors: '#64748b', fontSize: '12px' } } },
  yaxis: { labels: { style: { colors: '#64748b', fontSize: '12px' }, formatter: v => `${v} kWh` } },
  grid: { borderColor: '#e2e8f0', strokeDashArray: 4 },
  tooltip: { theme: 'light', y: { formatter: v => `${v} kWh` } },
  markers: { size: 5, colors: ['#10b981'], strokeColors: '#fff', strokeWidth: 2, hover: { size: 7 } }
}))

// Resources
const resources = ref([
  { name: 'esg.resources.lighting', value: '847 kWh', percentage: 78, color: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', iconPath: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z' },
  { name: 'esg.resources.hvac', value: '1,245 kWh', percentage: 65, color: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { name: 'esg.resources.equipment', value: '755 kWh', percentage: 82, color: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', iconPath: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z' }
])

// Space Chart
const spaceChartSeries = ref([{ name: t('esg.social.space.name'), data: [45, 78, 62, 85, 55, 70, 68] }])
const spaceChartOptions = computed(() => ({
  chart: { type: 'bar', height: 300, toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
  colors: ['#3b82f6'],
  plotOptions: { bar: { borderRadius: 8, columnWidth: '60%' } },
  dataLabels: { enabled: true, formatter: v => `${v}%`, style: { fontSize: '12px', fontWeight: 600, colors: ['#fff'] } },
  xaxis: { categories: t('esg.common.weekdays').split(','), labels: { style: { colors: '#64748b', fontSize: '12px' } } },
  yaxis: { labels: { style: { colors: '#64748b', fontSize: '12px' }, formatter: v => `${v}%` } },
  grid: { borderColor: '#e2e8f0', strokeDashArray: 4 },
  tooltip: { theme: 'light', y: { formatter: v => `${v}% ${t('esg.social.space.utilization')}` } }
}))

// Wellbeing
const wellbeingMetrics = ref([
  { label: 'esg.wellbeing.comfort', value: '94%', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#10b981', iconPath: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
  { label: 'esg.wellbeing.lighting', value: '520 lux', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#f59e0b', iconPath: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
  { label: 'esg.wellbeing.noise', value: '38 dB', gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#3b82f6', iconPath: 'M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z' },
  { label: 'esg.wellbeing.airflow', value: 'Optimal', gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', color: '#06b6d4', iconPath: 'M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5' }
])

// Compliance Items — FULLY TRANSLATED
const complianceItems = ref([
  { name: 'esg.compliance.energyMonitoring', detail: 'esg.governance.compliance.realtime', status: 'active', date: t('esg.common.24_7') },
  { name: 'esg.compliance.dataReporting', detail: 'esg.governance.compliance.automated', status: 'active', date: t('esg.common.daily') },
  { name: 'esg.compliance.accessControl', detail: 'esg.governance.compliance.encrypted', status: 'active', date: t('esg.common.active') },
  { name: 'esg.compliance.maintenance', detail: 'esg.governance.compliance.scheduled', status: 'pending', date: t('esg.compliance.nextMaintenanceDate') }
])

// Automation Chart
const automationChartSeries = ref([85, 12, 3])
const automationChartOptions = computed(() => ({
  chart: { type: 'donut', height: 280, fontFamily: 'Inter, sans-serif' },
  labels: [t('esg.governance.automation.automated'), t('esg.governance.automation.manual'), t('esg.governance.automation.scheduled')],
  colors: ['#10b981', '#64748b', '#f59e0b'],
  legend: { position: 'bottom', fontSize: '13px', fontWeight: 600, labels: { colors: '#64748b' } },
  dataLabels: { enabled: true, style: { fontSize: '14px', fontWeight: 700 } },
  plotOptions: {
    pie: {
      donut: {
        size: '70%',
        labels: {
          show: true,
          name: { show: true, fontSize: '16px', fontWeight: 700, color: '#1e293b' },
          value: { show: true, fontSize: '28px', fontWeight: 800, color: '#10b981', formatter: v => `${v}%` },
          total: { show: true, label: t('esg.governance.automation.total'), fontSize: '14px', fontWeight: 600, color: '#64748b', formatter: () => '85%' }
        }
      }
    }
  },
  tooltip: { theme: 'light', y: { formatter: v => `${v}%` } }
}))
</script>

<style scoped>
/* Base Container */
.esg-analytics-container {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
  overflow-x: hidden;
}

/* Animated Background */
.animated-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: floatGentle 20s ease-in-out infinite;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%);
  top: -10%;
  left: -10%;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%);
  top: 40%;
  right: -5%;
  animation-delay: 2s;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%);
  bottom: 10%;
  left: 20%;
  animation-delay: 4s;
}

.gradient-mesh {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(at 20% 30%, rgba(16, 185, 129, 0.08) 0px, transparent 50%),
    radial-gradient(at 80% 70%, rgba(59, 130, 246, 0.06) 0px, transparent 50%);
  animation: meshRotate 30s linear infinite;
}

@keyframes floatGentle {
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(30px, -30px) scale(1.05); }
  50% { transform: translate(-20px, 20px) scale(0.95); }
  75% { transform: translate(20px, 30px) scale(1.02); }
}

@keyframes meshRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.esg-content-wrapper {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 3rem 6rem;
}

/* Hero Section */
.esg-hero {
  text-align: center;
  padding: 3rem 0 4rem;
  animation: fadeInUp 0.8s ease-out;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.8) 100%);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 50px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.08);
  backdrop-filter: blur(10px);
}

.badge-icon {
  color: #10b981;
  animation: sparkle 2s ease-in-out infinite;
}

@keyframes sparkle {
  0%, 100% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.1); }
}

.badge-text {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #10b981;
}

.hero-title {
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 20px 0;
  letter-spacing: -2px;
  background: linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: clamp(1rem, 2vw, 1.2rem);
  line-height: 1.8;
  color: #64748b;
  max-width: 900px;
  margin: 0 auto;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Metrics Section */
.metrics-section {
  margin-bottom: 5rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.metric-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%);
  border-radius: 20px;
  padding: 24px;
  border: 1px solid rgba(16, 185, 129, 0.1);
  box-shadow: 
    0 4px 16px rgba(0, 0, 0, 0.04),
    0 1px 3px rgba(0, 0, 0, 0.02);
  backdrop-filter: blur(10px);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  opacity: 0;
  animation: fadeInUp 0.6s ease-out forwards;
}

.metric-card:hover {
  transform: translateY(-8px);
  box-shadow: 
    0 20px 40px rgba(16, 185, 129, 0.12),
    0 8px 16px rgba(16, 185, 129, 0.08);
  border-color: #10b981;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.metric-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.metric-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
}

.metric-trend.positive {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.metric-trend.neutral {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.metric-label {
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
}

.metric-value {
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -1px;
}

.metric-unit {
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
}

.metric-description {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}

/* Feature Section */
.feature-section {
  margin-bottom: 5rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 3rem;
}

.section-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.section-icon.environmental {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.section-icon.social {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.section-icon.governance {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
}

.section-text {
  flex: 1;
}

.section-title {
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px 0;
  letter-spacing: -1px;
}

.section-subtitle {
  font-size: 16px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
}

/* Feature Content */
.feature-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 28px;
}

.analytics-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%);
  border-radius: 24px;
  padding: 28px;
  border: 1px solid rgba(16, 185, 129, 0.1);
  box-shadow: 
    0 4px 16px rgba(0, 0, 0, 0.04),
    0 1px 3px rgba(0, 0, 0, 0.02);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.analytics-card.large {
  grid-column: 1 / -1;
}

.analytics-card:hover {
  transform: translateY(-4px);
  box-shadow: 
    0 12px 32px rgba(16, 185, 129, 0.08),
    0 4px 12px rgba(16, 185, 129, 0.06);
  border-color: #10b981;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.header-left {
  flex: 1;
}

.card-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.card-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.header-badge.success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.analytics-visual {
  position: relative;
  height: 200px;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
}

.feature-bg-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visual-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 100%);
  display: flex;
  align-items: flex-end;
  padding: 20px;
}

.stat-boxes {
  display: flex;
  gap: 20px;
  width: 100%;
}

.stat-box {
  flex: 1;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-value {
  font-size: 28px;
  font-weight: 800;
  color: white;
  letter-spacing: -1px;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-description {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
}

.card-description p {
  margin: 0;
}

.card-header-simple {
  margin-bottom: 20px;
}

.card-title-simple {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.chart-wrapper {
  margin: 0 -12px;
}

/* Climate Grid */
.climate-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.climate-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(16, 185, 129, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.1);
}

.climate-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.climate-icon.temp {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.climate-icon.humidity {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
}

.climate-icon.co2 {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.climate-icon.occupancy {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.climate-data {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.climate-value {
  font-size: 22px;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.5px;
}

.climate-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-footer-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  font-size: 13px;
  color: #64748b;
}

.card-footer-info svg {
  color: #10b981;
}

/* Resource List */
.resource-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.resource-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.resource-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.resource-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.resource-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.resource-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.resource-value {
  font-size: 13px;
  color: #64748b;
}

.resource-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 140px;
}

.progress-bar-mini {
  height: 6px;
  flex: 1;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill-mini {
  height: 100%;
  border-radius: 10px;
  transition: width 1s ease-out;
}

.resource-percentage {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  min-width: 40px;
  text-align: right;
}

/* Wellbeing Grid */
.wellbeing-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.wellbeing-card {
  padding: 20px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.wellbeing-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.wellbeing-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.wellbeing-value {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;
}

/* Compliance List */
.compliance-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.compliance-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.compliance-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.compliance-status {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.compliance-status.active {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.compliance-status.pending {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.compliance-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.compliance-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.compliance-detail {
  font-size: 13px;
  color: #64748b;
}

.compliance-date {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

/* CTA Section */
.cta-section {
  margin-bottom: 2rem;
}

.cta-content {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 32px;
  padding: 64px 48px;
  text-align: center;
  color: white;
  box-shadow: 0 24px 64px rgba(16, 185, 129, 0.25);
  position: relative;
  overflow: hidden;
}

.cta-content::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: pulse 4s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.8; }
}

.cta-icon {
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}

.cta-title {
  font-size: 36px;
  font-weight: 800;
  margin: 0 0 16px 0;
  letter-spacing: -1px;
  position: relative;
  z-index: 1;
}

.cta-description {
  font-size: 18px;
  line-height: 1.7;
  margin: 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.95;
  position: relative;
  z-index: 1;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .feature-content {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .esg-content-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }

  .esg-hero {
    padding: 2rem 0 3rem;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .climate-grid {
    grid-template-columns: 1fr;
  }

  .wellbeing-grid {
    grid-template-columns: 1fr;
  }

  .stat-boxes {
    flex-direction: column;
  }

  .cta-content {
    padding: 48px 32px;
  }
}

@media (max-width: 480px) {
  .esg-content-wrapper {
    padding: 1rem 1rem 3rem;
  }

  .analytics-card {
    padding: 20px;
  }

  .cta-content {
    padding: 40px 24px;
  }

  .cta-title {
    font-size: 28px;
  }
}
</style>